import styles from "./styles.module.scss";
import { Dispatch, ReactNode, SetStateAction, useState, useEffect } from "react";
import classnames from "classnames";
import { Textarea } from "@altimateai/lego";
import { Button } from "@altimateai/lego";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getAIFeedback, postAIFeedback } from "../../helpers/apis";
import { RelativeComponentLoader } from "../loader";
import classNames from "classnames";
import {
  IconThumbDown,
  IconThumbDownFilled,
  IconThumbUp,
  IconThumbUpFilled,
} from "@tabler/icons-react";
import { Stack } from "@altimateai/lego";

const GET_AI_FEEDBACK = "GET-AI-FEEDBACK";

interface FeedbackFormItem {
  label: string;
  type: string;
  value: string | number | null;
}

interface ChatFeedbackSubmitProps {
  onSubmit: () => void;
}

const ChatFeedbackSubmit = ({ onSubmit }: ChatFeedbackSubmitProps) => {
  const [isInputDisabled, setIsInputDisabled] = useState(false);

  const handleSubmit = () => {
    onSubmit();
    setIsInputDisabled(true);
  };

  return (
    <Stack align="start" direction="column">
      <div className={styles.feedback_title}>Many thanks for your feedback!</div>
      <Stack align="center">
        <Button
          className={classNames(styles.feedback_submit_comp, "mb-2")}
          onClick={handleSubmit}
          disabled={isInputDisabled}
        >
          Start new Agent
        </Button>
      </Stack>
    </Stack>
  );
};

interface FeedbackFormProps {
  formInputs: FeedbackFormItem[];
  setFormInputs: Dispatch<SetStateAction<FeedbackFormItem[]>>;
  isInputDisabled: boolean;
}

const FeedbackForm = ({
  formInputs,
  setFormInputs,
  isInputDisabled,
}: FeedbackFormProps): ReactNode => {
  const SAVED_TIME = [
    { label: "5 minutes", value: 5 },
    { label: "15 minutes", value: 15 },
    { label: "30 minutes", value: 30 },
    { label: "1 hour", value: 60 },
  ];
  return formInputs.map(item => (
    <>
      {item.type === "textTime" && (
        <>
          <div className={classnames(styles.feedback_title, "mt-2")}>{item.label}</div>
          <Stack className="al-mb-4" align="center">
            {SAVED_TIME.map(time => {
              return (
                <Button
                  disabled={isInputDisabled}
                  variant="outline"
                  className={classnames(styles.saved_time, {
                    [styles.selected]: time.value === item.value,
                    [styles.unselected]: time.value !== item.value,
                  })}
                  key={time.value}
                  onClick={() => {
                    setFormInputs(_inputs =>
                      _inputs.map(i => {
                        if (i.label === item.label)
                          i.value = time.value === i.value ? null : time.value;
                        return i;
                      })
                    );
                  }}
                >
                  {time.label}
                </Button>
              );
            })}
            <span className={styles.saved_time_text}>Or</span>
            <Textarea
              key={item.label}
              disabled={isInputDisabled}
              className={styles.feedback_input_comp}
              onChange={e =>
                setFormInputs(_inputs =>
                  _inputs.map(i => {
                    if (i.label === item.label) i.value = e.target.value;
                    return i;
                  })
                )
              }
              value={item.value ?? ""}
            />
            <span className={styles.saved_time_text}>minutes</span>
          </Stack>
        </>
      )}
      {item.type === "text" && (
        <Textarea
          key={item.label}
          disabled={isInputDisabled}
          className={styles.feedback_input_comp}
          placeholder={item.label || "Need more accuracy..."}
          aria-label="Feedback details"
          onChange={e =>
            setFormInputs(_inputs =>
              _inputs.map(i => {
                if (i.label === item.label) i.value = e.target.value;
                return i;
              })
            )
          }
          value={item.value ?? ""}
        />
      )}
    </>
  ));
};

interface FeedbackProps {
  feedback_id: string;
  feedback_type: string;
  resetWorkflow: () => void;
  afterSubmit?: () => void;
  className?: string;
  hideTitle?: boolean;
}

const Feedback = ({
  feedback_id,
  feedback_type,
  resetWorkflow,
  afterSubmit,
  className,
  hideTitle = false,
}: FeedbackProps) => {
  // state === 0 means like button active
  // state === 1 means dislike button active
  const [state, setState] = useState(-1);
  const [isInputDisabled, setIsInputDisabled] = useState(false);
  const [goodFormInputs, setGoodFormInputs] = useState<FeedbackFormItem[]>([]);
  const [badFormInputs, setBadFormInputs] = useState<FeedbackFormItem[]>([]);
  const like = state === 0;
  const dislike = state === 1;
  // Feedback for datapilot won't have an option of updatng once submitted.
  const isFeedbackDataPilot = feedback_type === "datapilot";

  const { data, isLoading } = useQuery({
    queryKey: [GET_AI_FEEDBACK, feedback_id, feedback_type],
    queryFn: () => getAIFeedback({ feedback_id, feedback_type }),
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (data) {
      if (data.is_submitted) {
        setIsInputDisabled(true);
        setState(data.feedback_value === "good" ? 0 : 1);
      }
      setGoodFormInputs(data.full_form.good_form);
      setBadFormInputs(data.full_form.bad_form);
    }
  }, [data]);

  const { mutate, data: feedbackUpdateResponse } = useMutation({
    mutationFn: postAIFeedback({ feedback_id, feedback_type }),
    onSuccess: afterSubmit,
  });

  const handleSubmit = () => {
    mutate({
      feedback_value: like ? "good" : "bad",
      full_form: {
        good_form: goodFormInputs.map(item => ({
          label: item.label,
          type: item.type,
          value: item.value !== null ? String(item.value) : "",
        })),
        bad_form: badFormInputs.map(item => ({
          label: item.label,
          type: item.type,
          value: item.value !== null ? String(item.value) : "",
        })),
      },
    });
    setIsInputDisabled(true);
    if (afterSubmit) {
      setState(-1);
    }
  };

  const resetFormUpdater = (_input: FeedbackFormItem[]): FeedbackFormItem[] => {
    const newInputs = [..._input];
    newInputs.forEach(item => (item.value = ""));
    return newInputs;
  };

  const handleCancel = () => {
    if (isInputDisabled && isFeedbackDataPilot) return;
    setState(-1);
    setGoodFormInputs(resetFormUpdater);
    setBadFormInputs(resetFormUpdater);
  };

  if (isLoading) {
    return <RelativeComponentLoader />;
  }

  if (data?.is_submitted || feedbackUpdateResponse?.is_submitted) {
    return (
      <Stack
        direction="column"
        className={classnames("gap-md al-items-left pt-2 pb-2 ps-3 pe-3", styles.feedback_card)}
      >
        Feedback is submitted
      </Stack>
    );
  }

  return (
    <Stack
      direction="column"
      className={classnames("gap-md al-items-left p-2", styles.feedback_card, className)}
      role="region"
      aria-label="Feedback section"
    >
      <Stack align="center" className="al-gap-2">
        {!hideTitle && <div className={styles.feedback_title}>Feedback</div>}
        <Button
          size="icon"
          variant={like ? "default" : "ghost"}
          aria-label="Like"
          title="Like"
          onClick={() => {
            if (isInputDisabled && isFeedbackDataPilot) return;
            setGoodFormInputs(resetFormUpdater);
            setBadFormInputs(resetFormUpdater);
            setState(0);
            setIsInputDisabled(false);
          }}
        >
          {like ? <IconThumbUpFilled /> : <IconThumbUp />}
        </Button>
        <Button
          size="icon"
          variant={dislike ? "default" : "ghost"}
          aria-label="Dislike"
          title="Dislike"
          onClick={() => {
            if (isInputDisabled && isFeedbackDataPilot) return;
            setGoodFormInputs(resetFormUpdater);
            setBadFormInputs(resetFormUpdater);
            setState(1);
            setIsInputDisabled(false);
          }}
        >
          {dislike ? <IconThumbDownFilled /> : <IconThumbDown />}
        </Button>
      </Stack>
      {like && (
        <FeedbackForm
          formInputs={goodFormInputs}
          setFormInputs={setGoodFormInputs}
          isInputDisabled={isInputDisabled}
        />
      )}
      {dislike && (
        <FeedbackForm
          formInputs={badFormInputs}
          setFormInputs={setBadFormInputs}
          isInputDisabled={isInputDisabled}
        />
      )}
      {state !== -1 && (
        <Stack gap="sm" align="center">
          <Button onClick={handleSubmit} disabled={isInputDisabled}>
            Submit
          </Button>
          <Button disabled={isInputDisabled} variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
        </Stack>
      )}
      {isInputDisabled && isFeedbackDataPilot && <ChatFeedbackSubmit onSubmit={resetWorkflow} />}
    </Stack>
  );
};

export { Feedback };
